import { ApiError } from '../../checkout/api/checkoutApi';
import { BranchesResponse, DeliveryPaymentState } from '../types';

/**
 * Klient API kroku 2 (/api/v1/checkout/delivery-payment). Stejné konvence
 * jako checkoutApi: session cookie same-origin, X-Requested-With: fetch (CSRF),
 * envelope {ok, data, error}.
 */
/** Brána při deployi / přetížení: krátké okno, kdy request nedojde do PHP. */
const TRANSIENT_STATUSES = [502, 503, 504];
const TRANSIENT_RETRY_MS = 2500;

const wait = (ms: number, signal?: AbortSignal) => new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
        window.clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
});

async function call<T>(url: string, options: RequestInit = {}, signal?: AbortSignal, retry = true): Promise<T> {
    const response = await fetch(url, {
        credentials: 'same-origin',
        signal,
        ...options,
        headers: {
            // 'XMLHttpRequest' kvůli Tracy isAjax() — viz checkoutApi.ts
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            ...(options.headers ?? {}),
        },
    });

    // Všechny endpointy kroku 2 nastavují absolutní stav (země, doprava, platba,
    // pokračovat) — jeden tichý opakovaný pokus je bezpečný
    if (retry && TRANSIENT_STATUSES.includes(response.status)) {
        await wait(TRANSIENT_RETRY_MS, signal);

        return call<T>(url, options, signal, false);
    }

    const body = await response.json().catch(() => null);

    if (!body || typeof body.ok !== 'boolean') {
        throw new ApiError(response.status, 'INVALID_RESPONSE', 'Unexpected server response');
    }

    if (!body.ok) {
        throw new ApiError(
            response.status,
            body.error?.code ?? 'UNKNOWN',
            body.error?.message ?? 'Unknown error',
            body.error?.fields,
        );
    }

    return body.data as T;
}

export function createDeliveryPaymentApi(baseUrl: string) {
    return {
        getState: (signal?: AbortSignal) =>
            call<DeliveryPaymentState>(`${baseUrl}/`, {}, signal),

        changeCountry: (countryId: number) =>
            call<DeliveryPaymentState>(`${baseUrl}/country`, {
                method: 'POST',
                body: JSON.stringify({ countryId }),
            }),

        // resolveConflict: vědomý klik na nedostupnou volbu — server odebere
        // konfliktní protistranu z košíku místo 409
        selectDelivery: (deliveryId: number, suboptionId?: number, resolveConflict?: boolean) =>
            call<DeliveryPaymentState>(`${baseUrl}/delivery`, {
                method: 'POST',
                body: JSON.stringify({
                    deliveryId,
                    ...(suboptionId != null ? { suboptionId } : {}),
                    ...(resolveConflict ? { resolveConflict: true } : {}),
                }),
            }),

        selectPayment: (paymentId: number, suboptionId?: number, resolveConflict?: boolean) =>
            call<DeliveryPaymentState>(`${baseUrl}/payment`, {
                method: 'POST',
                body: JSON.stringify({
                    paymentId,
                    ...(suboptionId != null ? { suboptionId } : {}),
                    ...(resolveConflict ? { resolveConflict: true } : {}),
                }),
            }),

        branches: (
            params: {
                deliveryId?: number;
                lat?: number;
                lng?: number;
                bounds?: { swLat: number; swLng: number; neLat: number; neLng: number };
            },
            signal?: AbortSignal,
        ) => {
            const query = new URLSearchParams();
            if (params.deliveryId != null) query.set('deliveryId', String(params.deliveryId));
            if (params.lat != null && params.lng != null) {
                query.set('lat', String(params.lat));
                query.set('lng', String(params.lng));
            }
            if (params.bounds) {
                query.set('swLat', String(params.bounds.swLat));
                query.set('swLng', String(params.bounds.swLng));
                query.set('neLat', String(params.bounds.neLat));
                query.set('neLng', String(params.bounds.neLng));
            }
            const qs = query.toString();

            return call<BranchesResponse>(`${baseUrl}/branches${qs ? `?${qs}` : ''}`, {}, signal);
        },

        continueToNext: () =>
            call<{ redirect: string }>(`${baseUrl}/continue`, { method: 'POST', body: '{}' }),
    };
}

export type DeliveryPaymentApi = ReturnType<typeof createDeliveryPaymentApi>;
